// Third-party libraries
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Badge, Button, Card, Flex,  Space, Table, Tag } from 'antd';
import { BellOutlined, CloseOutlined, EyeOutlined, UndoOutlined, 
    // VideoCameraFilled 
} from '@ant-design/icons';

// Constants
import { STATUS } from '../../../../constants/template';

// Components
import SVG from '../../../SVG';

// Handlers
import { table_handler } from '../../../../handlers';

// Services
import { REDUX, TIME } from '../../../../services/util';

// Redux actions
import * as REDUX_ACTIONS from '../../../../services/redux/actions'

// Styles
import styles from './index.module.css'; 

const { getColumnSearchProps } = table_handler

const {
    EVENT_TICKET_COLOR,
    EVENT_TICKET_TYPES_BY_CODES
} = STATUS

const NotificationsDrawer = () => {

    const dispatch = useDispatch()

    const vehicles = useSelector(REDUX.reduxStoreWrapper('vehicles.staticsByID'))
    const drivers = useSelector(REDUX.reduxStoreWrapper('drivers.byID'))
    const selectedVID = useSelector(REDUX.reduxStoreWrapper('vehicles.selectedID'))
    const vehicleEventLogs = useSelector(REDUX.reduxStoreWrapper('vehicleEventLogs'))

    const vehicleIDs = Object.keys(vehicles)

    const user = useSelector(REDUX.reduxStoreWrapper('user'))
    const userRoles = useSelector(REDUX.reduxStoreWrapper('userRoles.byID'))
    const loading = useSelector(REDUX.reduxStoreWrapper('style.loading.vehicleEvent'))

    const notifs = useMemo(() => 
        (selectedVID ? vehicleEventLogs.logByVID[selectedVID] : Object.values(vehicleEventLogs.logByVID).flatMap(notif => notif))
        ?.map((notif, i) => {

            return { 
                key: `${notif.eventid}#${i}`, 
                ...notif.rule, 
                ...notif, 
                vid: notif.vid,
                mdvrid: vehicles[notif.vid]?.mdvrid,
                lat: notif.location?.lat, 
                lon: notif.location?.lon, 
                generatedAt: notif.vehicleTime, 
                time: TIME.parseTimeFNS(notif.vehicleTime), 
                plate: vehicles[notif.vid]?.plate, 
                driver: drivers[notif.drvid]?.name || notif.drvid || '-',
                attachments: notif.attachments?.length ? notif.attachments : null,
                device: (notif.eventid || '').includes('cms') ? 'MDVR' : 'GPS'
            }
        })
        ?.filter(notif => notif.io === 1) || []
    , [selectedVID, vehicleEventLogs, drivers, vehicles])

    const searchTextState = useState('')
    const searchedColumnState = useState('')

    const [ showCard, setShowCard ] = useState(true)

    const getEventLog = (vid) => {
        vid
        &&
        dispatch(REDUX_ACTIONS.v3_vehicleLogs.get_vehicle_event_log_by_timeframe_request(
            vid,
            TIME.parseStartOfDate(),
            TIME.getCurTime(),
        ))
    }

    const text = (data) => {

        const { name: uname, uroleid } = user
        const { plate, name: event, time } = data

        const tempText = `Hi I am ${uname}, as ${userRoles[uroleid]?.roleName || uroleid}. I need help for vehicle *${plate}* for event *${event}* at *${time}*`

        return `https://api.whatsapp.com/send?phone=60137439285&text=${encodeURIComponent(tempText)}`
    }

    const columns = [
        {
          title: "Date",
          fixed: 'left',
        //   sorter: (a, b) => a.generatedAt - b.generatedAt,
          render: data => data.time
        //   ...getColumnSearchProps("Date", 'time', searchTextState, searchedColumnState),
        },
        {
          title: "Vehicle",
          dataIndex: "plate",
          ...getColumnSearchProps("Vehicle", 'plate', searchTextState, searchedColumnState),
        },
        {
          title: "Driver",
          dataIndex: "driver",
          ...getColumnSearchProps("Driver", 'driver', searchTextState, searchedColumnState),
        },
        {
            title: "Event Type",
            // ...getColumnSearchProps("Event Type", 'type', searchTextState, searchedColumnState),
            dataIndex: 'type_rule',
            filters: Object.keys(EVENT_TICKET_TYPES_BY_CODES).map(et => ({
                text: <Tag color={EVENT_TICKET_COLOR[EVENT_TICKET_TYPES_BY_CODES[et]]}>{EVENT_TICKET_TYPES_BY_CODES[et]}</Tag>,
                value: et
            })),
            onFilter: (value, record) => record.type_rule === Number(value),
            render: data => {
                return (
                    <Tag color={EVENT_TICKET_COLOR[EVENT_TICKET_TYPES_BY_CODES[data]]}>{EVENT_TICKET_TYPES_BY_CODES[data]}</Tag>
                )
            }
          },
                    {
            title: "Event",
            dataIndex: "device",
            ...getColumnSearchProps("Event", 'device', searchTextState, searchedColumnState),
        },
          {
            title: "Event",
            dataIndex: "name",
            ...getColumnSearchProps("Event", 'name', searchTextState, searchedColumnState),
        },
        {
            title: "View",
            render: (data) => 
                <EyeOutlined onClick={() => {
                    dispatch(REDUX_ACTIONS.v3_liveCamera.unset_selected_live_camera_by_vid_request())
                    dispatch(REDUX_ACTIONS.v3_vehicleLogs.set_selected_event_log_data(data))
                }} />
        },
        // {
        //     title: "Video",
        //     render: (data) => {
        //         return data.attachments 
        //             && 
        //             <VideoCameraFilled style={{ color: 'orange' }} onClick={() => dispatch(REDUX_ACTIONS.v3_liveCamera.set_selected_live_camera_by_vid_request(data.vid, data.mdvrid, false, data.attachments))} />
        //             ||
        //             '-'
        //     }
        // },
        {
            title: "Remarks",
            dataIndex: "remark",
            ...getColumnSearchProps("Remarks", 'remark', searchTextState, searchedColumnState),
            render: (data) => data || '-'
          },
        {
            title: "Actions",
            fixed: 'right',
            render: (data) => 
                <span
                    aria-hidden='true'
                    onClick={() => window.open(text(data), '_blank')}
                    style={{ cursor: 'pointer '}}
                >
                    <SVG 
                        viewBox="0 0 16 16" 
                        fill="none" 
                        size="20"
                        name={`remark`}
                    />
                </span>
            }
          
      ];

    //   const hasInit = useRef(false)

    const cancelRef = useRef(false)

    const getEventLogHDLR = (vid) => {

        if(vid === '') {
            cancelRef.current = false
            processPool(vehicleIDs)

        } else {
            getEventLog(vid)
        }
    }

    const clearNotificationHDLR = () => {
        dispatch(REDUX_ACTIONS.v3_notifications.clear_notifications_request())
        dispatch(REDUX_ACTIONS.v3_vehicleLogs.clear_vehicle_event_log_request())
    }

    const refreshNotificationHDLR = () => {
        getEventLogHDLR(selectedVID)
    }

    // const [ current, setCurrent ] = useState(1)
    // const [ pageSize, setPageSize ] = useState(10)
    
    // const paginationOnChangeHDLR = (current, pageSize) => {
    //     setCurrent(current)
    //     setPageSize(pageSize)
    // }

    // const startIndex = (current - 1) * pageSize; // pagination
    // const endIndex = startIndex + pageSize;

    // const dataSource = notifs?.sort((a, b) => b.generatedAt - a.generatedAt)?.slice(startIndex, endIndex)

    const dataSource = notifs?.sort((a, b) => b.generatedAt - a.generatedAt)

    // Defer the table render — React will skip re-rendering the table while
    // the user is interacting (e.g. clicking sidebar), keeping the UI responsive.
    // const dataSource = useDeferredValue(rawDataSource)

    const isInit = useRef(false)
    const requestedVidsRef = useRef(new Set()) 

    useEffect(() => {
        cancelRef.current = false

        if (vehicleIDs.length === 0) return

        let vidsToFetch = []

        if (!isInit.current && dataSource.length === 0) {
            vidsToFetch = vehicleIDs
            isInit.current = true
        } else {
            vidsToFetch = vehicleIDs.filter(vid =>
                !vehicleEventLogs?.[vid] && !requestedVidsRef.current.has(vid)
            )
        }

        if (vidsToFetch.length > 0) {
            vidsToFetch.forEach(vid => requestedVidsRef.current.add(vid))
            processPool(vidsToFetch)
        }

        return () => {
            cancelRef.current = true
        }
}, [vehicleIDs, vehicleEventLogs])

    // Worker pool: `limit` concurrent workers each await one vehicle's response
    // before pulling the next from the shared queue.
    // limit=1 → pure sequential (best for UI responsiveness with large fleets).
    // cancelRef stops every worker the moment the user navigates away.
    // function processPool(vids, limit = 1) {
    //     const startTime = TIME.parseStartOfDate()
    //     const endTime = TIME.getCurTime()

    //     const queue = vids.map(vid => ({ vid, startTime, endTime }))

    //     async function worker() {
    //         while (queue.length) {
    //             if (cancelRef.current) break
    //             const item = queue.shift()

    //             dispatch(REDUX_ACTIONS.v3_vehicleLogs.get_vehicle_event_log_by_timeframe_request(
    //                 item.vid, item.startTime, item.endTime
    //             ))
    //             await new Promise(resolve => setTimeout(resolve, 2000))
    //         }
    //     }

    //     Promise.all(Array(limit).fill().map(() => worker()))
    // }

    const processPool = (vids) => {

        const startTime = TIME.parseStartOfDate()
        const endTime = TIME.getCurTime()

        const formattedVIDS = vids.map(vid => ({ vid, startTime, endTime }))
        let queue = 0

        const callAPI = (item) => {

            dispatch(REDUX_ACTIONS.v3_vehicleLogs.get_vehicle_event_log_by_timeframe_request(item.vid, item.startTime, item.endTime, (data) => {
                queue++

                if(data && formattedVIDS[queue] && !cancelRef.current) setTimeout(() => callAPI(formattedVIDS[queue]), 2000)
                else return;
            }))
        }

        setTimeout(() => callAPI(formattedVIDS[0]), 1500)

    }


    return (
        <div>
            {/* <ModalRemark open={showRemark} onCancel={() => setShowRemark(false)} /> */}
            <div className={`${styles['svg-container']}`} aria-hidden='true' onClick={() => setShowCard(!showCard)}>
              <Badge dot={dataSource?.length || 0}>
                <BellOutlined style={{ fontSize: '1.5em'}}/>
              </Badge>
            </div>
                <Card className={`${styles['card-container']} ${showCard && styles['expanded-card']}`}>
                    <Flex justify='space-between' style={{ marginBottom: '12px' }}>
                        <h3>Total Notifications: {notifs?.length || 0}</h3>
                        
                        <Space>
                            <Button style={{ color: '#ACACAC' }} onClick={refreshNotificationHDLR} icon={<UndoOutlined />}>Refresh</Button>
                            <Button style={{ color: '#ACACAC' }} onClick={clearNotificationHDLR}>Clear All</Button>
                            <CloseOutlined onClick={() => setShowCard(false)} />
                        </Space>
                    </Flex>
                    <Table 
                        loading={loading}
                        dataSource={dataSource} 
                        columns={columns} 
                        // pagination={false}
                        pagination={{ position: ['bottomRight'] }}
                        scroll={{
                            y: 180,
                            x: columns && columns.length * 200
                        }}
                    />
                    {/* <Pagination style={{ float: 'right', marginTop: '12px' }} onChange={paginationOnChangeHDLR} total={notifs?.length} /> */}
                </Card>
        </div>
    )
}

export default React.memo(NotificationsDrawer);
